'use client';

import React, { FC } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import { Container } from '@/components/Container';
import { Label } from '@/components/Label/Label';
import { backgroundImageMixin } from '@/styles/BackgroundimageMixin';
import { devices } from '@/styles/breakpoints';
import { TitleH1, TitleH2 } from '@/styles/typography';

interface IProps {
  item: object;
}

export const Intro: FC<IProps> = ({ item, withVideo = true, imageSrc }) => {
  return (
    <SectionWrapper $withvideo={withVideo} imageSrc={imageSrc}>
      <Overlay>
        {withVideo && (
          <video
            autoPlay
            loop
            muted
            src="https://s3.us-west-1.amazonaws.com/staging.orcatec.storage/storage_61/media/NT2lzPBzWRHf8qqNpyEe051QoYqI2vx1B5JH9Bkl.mp4"
          >
            <source
              src="https://s3.us-west-1.amazonaws.com/staging.orcatec.storage/storage_61/media/NT2lzPBzWRHf8qqNpyEe051QoYqI2vx1B5JH9Bkl.mp4"
              type="video/mp4"
            />
            <track src="" kind="captions" label="english_captions" />
          </video>
        )}

        <Container>
          <WrapperInner>
            {item.tag && <TagText>{item.tag}</TagText>}

            <Title>
              <span className="first_part">{item.title.first_part} </span>
              <span className="second_part">
                {item.title.second_part} <Label title="Free" />
              </span>
            </Title>

            <Description>{item.description}</Description>
            <Button
              onClick={() =>
                window.open('https://app.orcatec.com/auth/signup', 'e_blank')
              }
              style={{ marginBottom: '32px' }}
              variant="secondary"
            >
              <p>Get Started Free</p>
            </Button>
            <ListItems>
              {item?.info?.map((item, idx, arr) => (
                <React.Fragment key={idx}>
                  <Item>
                    <p className="list-title">{item.title}</p>
                    <p className="list-description">{item.description}</p>
                  </Item>
                  {idx !== arr.length - 1 && (
                    <li>
                      <Divider />
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ListItems>
          </WrapperInner>
        </Container>
      </Overlay>
    </SectionWrapper>
  );
};

const SectionWrapper = styled.section<{
  $withvideo: boolean;
  imageSrc?: string;
}>`
  position: relative;
  width: 100%;
  height: 748px;
  ${(props) => (!props.$withvideo ? backgroundImageMixin(props.imageSrc) : '')};

  & video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: -250px 0;
    z-index: -1;
  }

  @media only screen and ${devices.desktop} {
    height: 748px;
    & video {
      object-position: center;
    }
  }
`;

const Overlay = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.55) 9.41%,
    rgba(0, 0, 0, 0) 81.67%,
    rgba(0, 0, 0, 0.55) 122.78%
  );
`;

const WrapperInner = styled.article`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  max-width: 564px;
`;

const TagText = styled(TitleH1)`
  font-family: var(--font-roboto);
  color: var(--whiteColor);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
  margin-bottom: 8px;
`;

const Title = styled(TitleH2)`
  font-family: var(--font-roboto);
  font-weight: 700;
  line-height: 32px;
  letter-spacing: 0em;
  text-align: left;
  margin-bottom: 12px;
  font-size: 28px;
  & .first_part {
    color: var(--mainOrange);
  }
  & .second_part {
    color: var(--whiteColor);
  }

  @media only screen and ${devices.desktop} {
    text-align: left !important;
    line-height: 58px;
    font-size: 48px;
  }
`;

const Description = styled.p`
  font-family: var(--font-roboto);
  color: var(--whiteColor);
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: left;
  margin-bottom: 32px;
`;

const ListItems = styled.ul`
  display: flex;
  list-style: none;
  gap: 24px;
  padding: 0;
`;
const Item = styled.li`
  color: var(--whiteColor);
  & .list-title {
    font-family: var(--font-roboto);
    color: var(--whiteColor);
    font-size: 24px;
    font-weight: 500;
    line-height: 36px;
    letter-spacing: 0em;
    text-align: left;
    margin-bottom: 8px;
  }
  & .list-description {
    font-family: var(--font-roboto);
    color: var(--whiteColor);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0em;
    text-align: left;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 60px;
  background-color: var(--whiteColor);
  opacity: 0.2;
  margin: 0 12px;
`;
